import { supabase } from './supabase'

/**
 * Syncs position_projects with trades, grouped by symbol (EF-104).
 *
 * Previous behavior (per-trade): one position_projects row per open trade.
 * With multiple Schwab accounts holding the same symbol, this accumulated
 * one row per (symbol, account) tuple. The Position Board view layer
 * (EF-93c) compensated by grouping `symbol::live`, but the DB still held
 * duplicates. EF-104 fixes the source: one project per symbol, with the
 * per-account breakdown captured in `account_sizing`.
 *
 * Behavior:
 *   1. Create ONE active position_projects row per symbol that has open
 *      trades but no existing project — `account_sizing` lists every
 *      account's shares + entry price.
 *   2. Promote effective Working projects to 'active' once trades appear for
 *      the symbol (EF-131), including legacy rows stored as `stage=idea` with
 *      `setup_status=working`. Updates entry_price / stop_price /
 *      account_sizing / trade_id / activated_at from real fill data;
 *      preserves all setup-phase fields.
 *   3. Close active position_projects whose symbol no longer has any
 *      open/partial trades. (Replaces the prior trade_id-based check,
 *      which broke when a project was linked to one trade among siblings.)
 *
 * Order-list absence never changes a Working setup to Idea. Retirement is
 * owned by the explicit setup cancellation flow with exact broker confirmation.
 */
export async function syncPositionProjects(userId) {
  if (!userId) return { existingProjects: [], tradesBySymbol: {} }

  const [{ data: openTrades }, { data: existingProjects }] = await Promise.all([
    supabase
      .from('trades')
      .select('id, symbol, asset_type, account_number, avg_entry_price, stop_loss, quantity_total, quantity_open, entry_date, entry_time, status')
      .eq('user_id', userId)
      .in('status', ['open', 'partial'])
      .order('entry_date', { ascending: true }),
    supabase
      .from('position_projects')
      .select('id, symbol, trade_id, stage, setup_status')
      .eq('user_id', userId)
      .or('stage.in.(active,working,planned),and(stage.eq.idea,setup_status.eq.working)'),
  ])

  if (!openTrades && !existingProjects) {
    return { existingProjects: [], tradesBySymbol: {} }
  }

  const { data: nicknames } = await supabase
    .from('account_nicknames')
    .select('account_number, nickname')
    .eq('user_id', userId)

  const nicknameMap = Object.fromEntries((nicknames || []).map(n => [n.account_number, n.nickname]))

  // Group open trades by symbol
  const tradesBySymbol = {}
  for (const t of (openTrades || [])) {
    if (!tradesBySymbol[t.symbol]) tradesBySymbol[t.symbol] = []
    tradesBySymbol[t.symbol].push(t)
  }

  const projectActivationPriority = (project) => {
    const stage = String(project?.stage || '').toLowerCase()
    const setupStatus = String(project?.setup_status || '').toLowerCase()
    if (stage === 'active') return 3
    if (stage === 'working' || (stage === 'idea' && setupStatus === 'working')) return 2
    if (stage === 'planned') return 1
    return 0
  }

  // Index existing activation candidates by symbol so we don't double-create.
  // Prefer an already-active identity when stale data contains both Active and
  // an effective Working setup; targeted repair owns merging those old rows.
  const projectBySymbol = {}
  for (const p of (existingProjects || [])) {
    if (!p.symbol) continue
    const current = projectBySymbol[p.symbol]
    if (!current || projectActivationPriority(p) > projectActivationPriority(current)) {
      projectBySymbol[p.symbol] = p
    }
  }

  const buildAccountSizing = (trades) => trades.map(t => ({
    account_number: t.account_number,
    account_name: nicknameMap[t.account_number] || ('···' + (t.account_number || '').slice(-4)),
    shares: t.quantity_open || t.quantity_total,
    entry_price: t.avg_entry_price,
  }))

  const deriveActivatedAt = (firstTrade) => firstTrade.entry_date
    ? (firstTrade.entry_time ? `${firstTrade.entry_date}T${firstTrade.entry_time}` : firstTrade.entry_date)
    : new Date().toISOString()

  // Walk symbols with open trades. Three outcomes per symbol:
  //   1. No project yet           → INSERT new active project
  //   2. Project at 'working'     → PROMOTE in place to 'active' (EF-131)
  //   3. Project at 'active'/etc. → skip (already up to date here; the
  //      separate close-loop below handles transitions out of 'active')
  const newProjects = []
  const promotionUpdates = []
  for (const [symbol, trades] of Object.entries(tradesBySymbol)) {
    const firstTrade = trades[0] // earliest by entry_date (query is ordered)
    const accountSizing = buildAccountSizing(trades)
    const activatedAt = deriveActivatedAt(firstTrade)
    const existing = projectBySymbol[symbol]

    if (existing) {
      const storedStage = String(existing.stage || '').toLowerCase()
      const setupStatus = String(existing.setup_status || '').toLowerCase()
      const isEffectiveWorking = storedStage === 'working'
        || (storedStage === 'idea' && setupStatus === 'working')

      if (isEffectiveWorking) {
        // EF-131: promote in place. Only fill-derived fields are touched —
        // setup-phase fields (thesis, setup_notes, market_conditions,
        // setup_type, setup_pattern, entry_type, trigger_price,
        // pattern_failure_price, direction, setup_source, etc.) are left
        // alone so the user's research is preserved.
        promotionUpdates.push({
          id: existing.id,
          symbol,
          patch: {
            stage: 'active',
            entry_price: firstTrade.avg_entry_price,
            stop_price: firstTrade.stop_loss,
            account_sizing: accountSizing,
            trade_id: firstTrade.id,
            activated_at: activatedAt,
            updated_at: new Date().toISOString(),
          },
        })
      }
      continue
    }

    newProjects.push({
      user_id: userId,
      symbol,
      asset_type: firstTrade.asset_type || 'equity',
      stage: 'active',
      entry_price: firstTrade.avg_entry_price,
      stop_price: firstTrade.stop_loss,
      account_sizing: accountSizing,
      trade_id: firstTrade.id,
      is_draft: false,
      activated_at: activatedAt,
    })
  }

  if (newProjects.length > 0) {
    await supabase.from('position_projects').insert(newProjects)
  }

  for (const { id, symbol, patch } of promotionUpdates) {
    await supabase.from('position_projects').update(patch).eq('id', id)
    console.log(`[syncPositionProjects] Promoted working → active: ${symbol}`)
  }

  // Close active projects whose symbol no longer has any open trades.
  // Symbol-based check (vs trade_id-based) is more robust now that one
  // project covers many trades.
  const activeSymbols = new Set(Object.keys(tradesBySymbol))
  const projectsToClose = (existingProjects || [])
    .filter(p => p.stage === 'active' && !activeSymbols.has(p.symbol))

  for (const p of projectsToClose) {
    await supabase
      .from('position_projects')
      .update({ stage: 'closed', closed_at: new Date().toISOString(), updated_at: new Date().toISOString() })
      .eq('id', p.id)
  }

  return { existingProjects: existingProjects || [], tradesBySymbol }
}
